import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { keywordFrequencies } from "./textUtils";
import { NoticeBox } from "@/components/NoticeBox";

const MAX_CHARS = 20000;

const TOOL_INFO_CONTENT = {
  what: `Analyzes a piece of text and calculates how frequently each word or phrase appears, expressed as a percentage of the total content — useful for checking whether content is naturally balanced or over-stuffed with a target keyword.`,
  how: `Paste your content (up to ${MAX_CHARS.toLocaleString()} characters), and the keyword frequency breakdown is calculated automatically, ranked by how often each term appears. Use Clear to start over with different text.`,
  tip: `There's no single "correct" keyword density — writing naturally for a human reader first, then checking this as a sanity check afterward, tends to produce better results than writing to hit a specific percentage target.`,
};

export default function KeywordDensity() {
  const [text, setText] = useState("");

  const [phraseLength, setPhraseLength] = useState<1 | 2 | 3>(1);
  const [skipCommon, setSkipCommon] = useState(true);
  const analysis = text.trim() ? keywordFrequencies(text, { phraseLength, skipCommon }) : { rows: [], totalWords: 0 };

  // Splits on sentence-ending punctuation followed by whitespace or the
  // end of the text -- a simple heuristic, not a full NLP sentence
  // boundary detector, so it will occasionally miscount things like
  // "Dr. Smith" or "3.5 million" as extra sentence breaks. Good enough
  // for a rough content-density check, not meant to be exact.
  const sentenceCount = text.trim()
    ? (text.match(/[^.!?]+[.!?]+(\s|$)/g) || (text.trim() ? [text] : [])).length
    : 0;
  const wordCount = analysis.totalWords;

  const results = analysis.rows;

  const { blocked, gate } = useLogToolUsage(text);
  const [truncated, setTruncated] = useState(false);
  const csv = ["Keyword,Count,Density %", ...results.map(r => `"${r.phrase.replace(/"/g, '""')}",${r.count},${r.density}`)].join("\n");
  const downloadCsv = async () => {
    if (!(await gate())) return;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "keyword-density.csv"; a.click();
    URL.revokeObjectURL(url);
  };


  return (
    <ToolContainer>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Paste your content</Label>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${text.length > MAX_CHARS ? "text-red-500" : "text-muted-foreground"}`}>
                {text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
              </span>
              {text && (
                <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs" onClick={() => setText("")}>
                  <X className="h-3 w-3" /> Clear
                </Button>
              )}
            </div>
          </div>
          <Textarea
            value={text}
            onChange={e => { setTruncated(e.target.value.length > MAX_CHARS); setText(e.target.value.slice(0, MAX_CHARS)); }}
            aria-label="Content to analyze"
            placeholder="Paste article or page content here..."
            className="min-h-[150px]"
          />
          {truncated && <NoticeBox className="text-xs mt-1" role="status">Your text was longer than {MAX_CHARS.toLocaleString()} characters, so only the first {MAX_CHARS.toLocaleString()} were analyzed.</NoticeBox>}
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <label className="flex items-center gap-2">Phrase length
            <select className="border border-border rounded-md bg-background px-2 py-1" value={phraseLength} onChange={e => setPhraseLength(Number(e.target.value) as 1 | 2 | 3)}>
              <option value={1}>1 word</option><option value={2}>2 words</option><option value={3}>3 words</option>
            </select>
          </label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={skipCommon} onChange={e => setSkipCommon(e.target.checked)} /> Ignore common English words (the, and, of…)</label>
        </div>
        {text.trim() && !blocked && (
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-lg border bg-card"><p className="text-lg font-bold text-primary">{wordCount.toLocaleString()}</p><p className="text-xs text-muted-foreground">Words</p></div>
            <div className="text-center p-3 rounded-lg border bg-card"><p className="text-lg font-bold text-primary">{sentenceCount.toLocaleString()}</p><p className="text-xs text-muted-foreground">Sentences</p></div>
            <div className="text-center p-3 rounded-lg border bg-card"><p className="text-lg font-bold text-primary">{sentenceCount > 0 ? (wordCount / sentenceCount).toFixed(1) : "0"}</p><p className="text-xs text-muted-foreground">Words / Sentence</p></div>
          </div>
        )}
        {!blocked && results.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted"><tr><th className="p-2 text-left">Keyword</th><th className="p-2 text-right">Count</th><th className="p-2 text-right">Density</th></tr></thead>
              <tbody>{results.map((r, i) => (
                <tr key={i} className="border-t"><td className="p-2 font-mono">{r.phrase}</td><td className="p-2 text-right">{r.count}</td><td className="p-2 text-right">{r.density}%</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {!blocked && results.length > 0 && <Button size="sm" variant="outline" onClick={downloadCsv}>Download CSV</Button>}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
